import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Newspaper } from 'lucide-react';
import { oddpoolService } from '@/services/oddpoolService';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import T from "@/i18n/T";

export default function DailyDigestCard() {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['ai-daily-digest'],
    queryFn: async () => {
      const today = new Date().toISOString().split('T')[0];
      const { data } = await supabase
        .from('ai_daily_digests')
        .select('*')
        .eq('digest_date', today)
        .maybeSingle();
      return data;
    },
  });

  const generate = async () => {
    try {
      await oddpoolService.aiDailyDigest();
      toast.success('Digest generated');
      refetch();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const payload = (data?.payload ?? {}) as {
    headline?: string; summary?: string;
    top_arbs?: Array<{ title: string; net_cents: number; why: string }>;
    whale_moves?: Array<{ title: string; narrative: string }>;
    shifts?: Array<{ title: string; change: string }>;
  };

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <Newspaper className="h-4 w-4 text-primary" /> AI Daily Digest
        </CardTitle>
        <Button size="sm" variant="outline" onClick={generate}><T k="auto.regenerate-b232" /></Button>
      </CardHeader>
      <CardContent>
        {isLoading ? <Skeleton className="h-24 w-full" /> : !data ? (
          <p className="text-sm text-muted-foreground"><T k="auto.no-digest-yet-click-regenerate-d188" /></p>
        ) : (
          <div className="space-y-3">
            {payload.headline && <h3 className="font-semibold">{payload.headline}</h3>}
            {payload.summary && <p className="text-sm text-muted-foreground">{payload.summary}</p>}
            {payload.top_arbs && payload.top_arbs.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold mb-1"><T k="auto.top-arbs-3030" /></h4>
                <ul className="text-xs space-y-1">
                  {payload.top_arbs.map((a, i) => (
                    <li key={i}>• <span className="font-medium">{a.title}</span>, {a.net_cents}¢, {a.why}</li>
                  ))}
                </ul>
              </div>
            )}
            {payload.whale_moves && payload.whale_moves.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold mb-1"><T k="auto.whale-moves-3d39" /></h4>
                <ul className="text-xs space-y-1">
                  {payload.whale_moves.map((w, i) => (
                    <li key={i}>• <span className="font-medium">{w.title}</span>, {w.narrative}</li>
                  ))}
                </ul>
              </div>
            )}
            {payload.shifts && payload.shifts.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold mb-1"><T k="auto.probability-shifts-4de8" /></h4>
                <ul className="text-xs space-y-1">
                  {payload.shifts.map((s, i) => (
                    <li key={i}>• <span className="font-medium">{s.title}</span>, {s.change}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
